import { ReactNode, useEffect, useMemo } from 'react';

import {
    calculateHourMinFormat,
    EXPANDED_COLUMN_WIDTH,
    formatDate,
    normalizeScore,
    renderScore,
    renderStatus,
    sortableHeader
} from 'pages/reports/components/ReportFunctions';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { ViewOptionsBar } from 'components/ViewOptionsBar/ViewOptionsBar';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Card, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { orderByComparator, SortDescriptor } from 'utils/dataQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { buildExportFileName } from 'utils';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/Report67/Report67.scss';
import 'pages/reports/components/Report4/styles/Report4.scss';

// One row of WPL3_GetCourseDetailsReportTableFormat as serialized by Report.SetResultsAsTable.
type Report4Row = {
    Attempts: number | null;
    Completed: { IsNull?: boolean; Share?: string } | null;
    DateCompleted: string | null;
    FileUploadScore: number | null;
    GradeStatusId: number | null;
    Highestscore: number | null;
    Highestscoreresultid: number | null;
    id_student: number | null;
    Lessonname: string;
    Lessonorder: number;
    Progress: number | null;
    ResourceId: number;
    Resourcename: string;
    Resourceorder: number;
    ResourceTypeID: number;

    // Null when the student's first or last name is NULL (SQL string concatenation).
    Studentname: string | null;
    TestPointsPossible: string | null;
    Testprogress: number | null;
    Testtotalminutesspent: number | null;
    Videototalminutesspent: number | null;
    VideoWatchCount: string | null;
};

type Report4Column = { field: string };

type ResourceColumn = {
    cellKey: string;
    ResourceId: number;
    Resourcename: string;
    Resourceorder: number;
};

type LessonColumn = {
    key: string;
    Lessonname: string;
    Lessonorder: number;
    resources: ResourceColumn[];
};

type CellStatus = 'Complete' | 'In Progress' | 'Not Started';

type CellSummary = {
    attempts: number;
    completed: boolean;
    dateCompleted: string | null;
    fileUploadScore: number | null;
    gradeStatusId: number | null;
    highestScore: number | null;
    progress: number | null;
    status: CellStatus;
    testMinutes: number;
    testPoints: string | null;
    videoMinutes: number;
    videoWatchCount: string | null;
};

type StudentRow = {
    cells: Record<string, CellSummary>;
    completedCount: number;
    studentKey: string;
    Studentname: string;
    totalMinutes: number;
};

type MetricType = 'Status' | 'Date Completed' | 'Score' | 'Points' | 'Attempts' | 'Time Spent' | 'Video Progress';

type Report4Props = {
    reportSections: ReportSection[];
};

const MAX_DISPLAY_ROWS = 500;
const CSV_LINK_ID = 'report4CsvLink';
const EMPTY = '---';
const STATUS_RANK: Record<CellStatus, number> = { 'Complete': 2, 'In Progress': 1, 'Not Started': 0 };

const sumOf = (values: (number | null)[]) => values.reduce<number>((sum, v) => sum + (v ?? 0), 0);

const maxOf = (values: (number | null)[]) => {

    const nums = values.filter((v): v is number => v !== null && v !== undefined);
    return nums.length ? Math.max(...nums) : null;

};

const hasPoints = (points: string | null) => !!points && points !== '0/0';

const REPORT4_FIELDS: (keyof Report4Row)[] = [
    'Attempts', 'Completed', 'DateCompleted', 'FileUploadScore', 'GradeStatusId', 'Highestscore', 'Highestscoreresultid',
    'id_student', 'Lessonname', 'Lessonorder', 'Progress', 'ResourceId', 'Resourcename', 'Resourceorder', 'ResourceTypeID',
    'Studentname', 'TestPointsPossible', 'Testprogress', 'Testtotalminutesspent', 'Videototalminutesspent', 'VideoWatchCount'
];

// Column names vary in casing/naming between proc versions, so match them loosely.
const FIELD_ALIASES: Partial<Record<keyof Report4Row, string[]>> = {
    id_student: ['StudentId', 'StudentProfileId', 'Student_Id'],
    Lessonname: ['Lesson', 'LessonTitle'],
    Lessonorder: ['LessonDisplayOrder'],
    Resourcename: ['Resource', 'ResourceTitle'],
    Resourceorder: ['ResourceDisplayOrder'],
    Studentname: ['StudentName', 'Student', 'FullName']
};

const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');

const toReport4Row = (raw: Record<string, unknown>): Report4Row => {

    const lookup = new Map(Object.entries(raw).map(([key, value]) => [normalizeKey(key), value]));
    const pick = (field: keyof Report4Row) => [field, ...(FIELD_ALIASES[field] ?? [])]
        .map(name => lookup.get(normalizeKey(name)))
        .find(value => value !== undefined) ?? null;

    return Object.fromEntries(REPORT4_FIELDS.map(field => [field, pick(field)])) as Report4Row;

};

const getStudentKey = (row: Report4Row) => row.id_student !== null && row.id_student !== undefined
    ? `id:${row.id_student}`
    : `name:${row.Studentname ?? ''}`;

// A student/resource pair can return several rows (one per test mode), so roll them up into one cell.
const summarizeCell = (records: Report4Row[]): CellSummary => {

    const highestScore = maxOf(records.map(r => r.Highestscore));
    const progress = maxOf(records.map(r => r.Progress));
    const bestScoreRecord = records.find(r => r.Highestscore === highestScore) ?? records[0];
    const bestVideoRecord = records.find(r => r.Progress === progress) ?? records[0];
    const completedDates = records
        .map(r => r.DateCompleted)
        .filter((d): d is string => !!d)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    const attempts = sumOf(records.map(r => r.Attempts));
    const testMinutes = sumOf(records.map(r => r.Testtotalminutesspent));
    const videoMinutes = sumOf(records.map(r => r.Videototalminutesspent));
    const completed = records.some(r => r.Completed?.Share === '1');
    const started = attempts > 0 || testMinutes > 0 || videoMinutes > 0 || (progress ?? 0) > 0;

    return {
        attempts,
        completed,
        dateCompleted: completedDates[0] ?? null,
        fileUploadScore: maxOf(records.map(r => r.FileUploadScore)),
        gradeStatusId: records.find(r => r.GradeStatusId !== null && r.GradeStatusId !== undefined)?.GradeStatusId ?? null,
        highestScore,
        progress,
        status: completed ? 'Complete' : started ? 'In Progress' : 'Not Started',
        testMinutes,
        testPoints: bestScoreRecord.TestPointsPossible,
        videoMinutes,
        videoWatchCount: bestVideoRecord.VideoWatchCount
    };

};

export const Report4 = (props: Report4Props) => {

    const { reportSections } = props;

    const [state, setState] = useSuperState({
        currentMetric: 'Status' as MetricType,
        exportFileName: 'DetailsReport.csv',
        sort: [] as SortDescriptor[]
    });

    const { currentMetric, exportFileName, sort } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const results = reportSections[0]?.Components[0]?.Results;
    const rawRows = useMemo<Record<string, unknown>[]>(() => results?.Data ?? [], [results]);
    const rows = useMemo(() => rawRows.map(toReport4Row), [rawRows]);
    const sourceColumns = useMemo<Report4Column[]>(() => results?.Columns ?? [], [results]);

    const statusLabel = (status: CellStatus) => status === 'Complete'
        ? formatMessage({ id: 'complete.6757643c', defaultMessage: 'Complete' })
        : status === 'In Progress'
            ? formatMessage({ id: 'in_progress.0e4b5895', defaultMessage: 'In Progress' })
            : formatMessage({ id: 'not_started.6c6f679c', defaultMessage: 'Not Started' });

    // Lesson -> resource column groups, in the course's lesson and resource display order.
    const lessonColumns = useMemo(() => {

        const lessonMap = new Map<string, LessonColumn & { resourceMap: Map<number, ResourceColumn> }>();
        rows.forEach(row => {

            const lessonKey = `${row.Lessonorder}|${row.Lessonname}`;
            if (!lessonMap.has(lessonKey)) {

                lessonMap.set(lessonKey, {
                    key: lessonKey,
                    Lessonname: row.Lessonname,
                    Lessonorder: row.Lessonorder ?? 0,
                    resourceMap: new Map(),
                    resources: []
                });

            }
            const lesson = lessonMap.get(lessonKey);
            if (lesson && !lesson.resourceMap.has(row.ResourceId)) {

                lesson.resourceMap.set(row.ResourceId, {
                    cellKey: `${lessonKey}|${row.ResourceId}`,
                    ResourceId: row.ResourceId,
                    Resourcename: row.Resourcename,
                    Resourceorder: row.Resourceorder ?? 0
                });

            }

        });

        return Array.from(lessonMap.values())
            .sort((a, b) => a.Lessonorder - b.Lessonorder)
            .map(({ resourceMap, ...lesson }) => ({
                ...lesson,
                resources: Array.from(resourceMap.values()).sort((a, b) => a.Resourceorder - b.Resourceorder)
            }));

    }, [rows]);

    const resourceCount = useMemo(() => lessonColumns.reduce((sum, l) => sum + l.resources.length, 0), [lessonColumns]);

    const students = useMemo(() => {

        const recordMap = new Map<string, { name: string; records: Record<string, Report4Row[]> }>();
        rows.forEach(row => {

            const studentKey = getStudentKey(row);
            if (!recordMap.has(studentKey)) recordMap.set(studentKey, { name: row.Studentname ?? '', records: {} });
            const student = recordMap.get(studentKey);
            const cellKey = `${row.Lessonorder}|${row.Lessonname}|${row.ResourceId}`;
            if (student) student.records[cellKey] = [...(student.records[cellKey] ?? []), row];

        });

        return Array.from(recordMap.entries()).map(([studentKey, student]): StudentRow => {

            const cells: Record<string, CellSummary> = {};
            Object.entries(student.records).forEach(([cellKey, records]) => {

                cells[cellKey] = summarizeCell(records);

            });
            const summaries = Object.values(cells);

            return {
                cells,
                completedCount: summaries.filter(c => c.completed).length,
                studentKey,
                Studentname: student.name,
                totalMinutes: sumOf(summaries.map(c => c.testMinutes + c.videoMinutes))
            };

        });

    }, [rows]);

    const getCellSortValue = (cell: CellSummary | undefined): number | string | null => {

        if (!cell) return null;
        switch (currentMetric) {

            case 'Status': return STATUS_RANK[cell.status];
            case 'Date Completed': return cell.dateCompleted ? new Date(cell.dateCompleted).getTime() : null;
            case 'Score': return cell.highestScore !== null ? normalizeScore(cell.highestScore) : cell.fileUploadScore;
            case 'Points': return hasPoints(cell.testPoints) ? Number(cell.testPoints?.split('/')[0]) : null;
            case 'Attempts': return cell.attempts;
            case 'Time Spent': return cell.testMinutes + cell.videoMinutes;
            case 'Video Progress': return cell.progress;
            default: return null;

        }

    };

    const getSortableValue = (student: StudentRow, field?: string) => {

        if (!field) return null;
        if (field.startsWith('cell:')) return getCellSortValue(student.cells[field.slice(5)]);
        if (field === 'completedCount') return student.completedCount;
        if (field === 'totalMinutes') return student.totalMinutes;
        return student.Studentname;

    };

    const sortedStudents = useMemo(
        () => orderByComparator(students, sort, getSortableValue),
        [students, sort, currentMetric]
    );
    const displayStudents = useMemo(() => sortedStudents.slice(0, MAX_DISPLAY_ROWS), [sortedStudents]);

    const handleSort = (field: string) => {

        const current = sort.find(s => s.field === field);
        const nextDir = !current ? 'asc' : current.dir === 'asc' ? 'desc' : undefined;
        setState({ sort: nextDir ? [{ dir: nextDir, field }] : [] });

    };

    // Export every column of the original report, one row per source record, in lesson/resource order.
    const exportRows = useMemo(() => rows
        .map((row, index) => ({ raw: rawRows[index], row }))
        .sort(({ row: a }, { row: b }) => ((a.Lessonorder ?? 0) - (b.Lessonorder ?? 0)) || ((a.Resourceorder ?? 0) - (b.Resourceorder ?? 0)) || (a.Studentname ?? '').localeCompare(b.Studentname ?? ''))
        .map(({ raw }) => {

            const record: Record<string, string | number | null> = {};
            sourceColumns.forEach(({ field }) => {

                const value = raw[field];
                record[field] = value !== null && typeof value === 'object'
                    ? ((value as { Share?: string }).Share ?? null)
                    : (value as string | number | null | undefined) ?? null;

            });
            return record;

        }), [rows, rawRows, sourceColumns]);

    const handleExportCSV = () => {

        setState({ exportFileName: buildExportFileName('Report', 'DetailsReport', 'csv') }, () => {

            document.getElementById(CSV_LINK_ID)?.click();

        });

    };

    const renderScoreValue = (cell: CellSummary): ReactNode => cell.highestScore !== null && cell.attempts > 0
        ? renderScore(cell.highestScore)
        : cell.fileUploadScore !== null
            ? renderScore(Math.round(cell.fileUploadScore))
            : EMPTY;

    const renderMetric = (cell: CellSummary): ReactNode => {

        switch (currentMetric) {

            case 'Status': return renderStatus(cell.status);
            case 'Date Completed': return cell.dateCompleted ? formatDate(cell.dateCompleted) : EMPTY;
            case 'Score': return renderScoreValue(cell);
            case 'Points': return hasPoints(cell.testPoints) ? cell.testPoints : EMPTY;
            case 'Attempts': return cell.attempts > 0 ? `${cell.attempts}x` : EMPTY;
            case 'Time Spent': return cell.testMinutes + cell.videoMinutes > 0 ? calculateHourMinFormat(cell.testMinutes + cell.videoMinutes) : EMPTY;
            case 'Video Progress': return cell.progress !== null ? `${cell.progress}%` : EMPTY;
            default: return EMPTY;

        }

    };

    const renderCellTooltip = (cell: CellSummary) => (
        <p style={{ textAlign: 'start' }}>
            <FormattedMessage defaultMessage="Status: {status}" id="status_status.7e3c5400" values={{ status: statusLabel(cell.status) }} />
            <br />
            <FormattedMessage defaultMessage="Date Completed: {date}" id="date_completed_date.04477ed2" values={{ date: cell.dateCompleted ? formatDate(cell.dateCompleted) : EMPTY }} />
            <br />
            <FormattedMessage defaultMessage="Score: {score}" id="score_score.d049d037" values={{ score: cell.highestScore !== null ? `${normalizeScore(cell.highestScore)}%` : EMPTY }} />
            <br />
            <FormattedMessage defaultMessage="Points: {points}" id="points_points.ccce89de" values={{ points: hasPoints(cell.testPoints) ? cell.testPoints : EMPTY }} />
            <br />
            <FormattedMessage defaultMessage="Attempts: {count}x" id="attempts_countx.c7ccf135" values={{ count: cell.attempts }} />
            <br />
            <FormattedMessage defaultMessage="Test Time: {time}" id="test_time_time.80658a69" values={{ time: calculateHourMinFormat(cell.testMinutes) }} />
            <br />
            <FormattedMessage defaultMessage="Video Time: {time}" id="video_time_time.173ed0d9" values={{ time: calculateHourMinFormat(cell.videoMinutes) }} />
            <br />
            <FormattedMessage
                defaultMessage="Video Progress: {progress} ({watched})" id="video_progress_progress_watched.1ee24c19"
                values={{ progress: cell.progress !== null ? `${cell.progress}%` : EMPTY, watched: cell.videoWatchCount ?? EMPTY }}
            />
            <br />
            <FormattedMessage defaultMessage="File Upload Score: {score}" id="file_upload_score_score.6bcb7612" values={{ score: cell.fileUploadScore !== null ? `${Math.round(cell.fileUploadScore)}%` : EMPTY }} />
            {cell.gradeStatusId !== null ? (
                <>
                    <br />
                    <FormattedMessage defaultMessage="Grade Status: {gradeStatus}" id="grade_status_gradestatus.cc7d5157" values={{ gradeStatus: cell.gradeStatusId }} />
                </>
            ) : null}
        </p>
    );

    const metricOptions: { label: string; metric: MetricType }[] = [
        { label: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }), metric: 'Status' },
        { label: formatMessage({ id: 'date_completed.c5bb5aee', defaultMessage: 'Date Completed' }), metric: 'Date Completed' },
        { label: formatMessage({ id: 'score.6ed6415a', defaultMessage: 'Score' }), metric: 'Score' },
        { label: formatMessage({ id: 'points.9b71f1bb', defaultMessage: 'Points' }), metric: 'Points' },
        { label: formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' }), metric: 'Attempts' },
        { label: formatMessage({ id: 'time_spent.da775c0c', defaultMessage: 'Time Spent' }), metric: 'Time Spent' },
        { label: formatMessage({ id: 'video_progress.ffe513ee', defaultMessage: 'Video Progress' }), metric: 'Video Progress' }
    ];
    const metricButtons = metricOptions.map(({ label, metric }) => ({ action: () => setState({ currentMetric: metric }), title: label }));

    useEffect(() => {

        if (students.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: students.length }
                ),
                'warning'
            );

        }

    }, [students]);

    return (
        <Card>
            <CSVLink className="d-none" data={exportRows} filename={exportFileName} id={CSV_LINK_ID} />
            <div className="p-3 d-flex justify-content-between">
                <ViewOptionsBar buttonGroups={[metricButtons]} />
                <div className="mt-4">
                    <GMetrixButton onClick={handleExportCSV} variant="primary">
                        <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                    </GMetrixButton>
                </div>
            </div>
            <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                <Table bordered hover className="kendo-table sticky-table-header report4-table mb-0">
                    <thead>
                        <tr>
                            <th
                                className="report4-table__corner"
                                onClick={() => handleSort('Studentname')}
                                rowSpan={2}
                                style={{ cursor: 'pointer', minWidth: 150 }}
                            >
                                {sortableHeader(<FormattedMessage defaultMessage="Student Name" id="student_name.f4347b21" />, 'Studentname', sort)}
                            </th>
                            <th className="tableHeader pt-0" colSpan={2}>
                                <FormattedMessage defaultMessage="Statistics" id="statistics.0d26e35d" />
                            </th>
                            {lessonColumns.map(lesson => (
                                <th
                                    className="tableHeader text-center"
                                    colSpan={lesson.resources.length}
                                    key={lesson.key}
                                    style={{ maxWidth: EXPANDED_COLUMN_WIDTH * lesson.resources.length }}
                                >
                                    <OverlayTrigger
                                        overlay={(
                                            <Tooltip id={`${lesson.key}-lesson`}>
                                                {lesson.Lessonname}
                                            </Tooltip>
                                        )} placement="top"
                                    >
                                        <div className="text-truncate">
                                            {lesson.Lessonname}
                                        </div>
                                    </OverlayTrigger>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            <th className="text-center" onClick={() => handleSort('completedCount')} style={{ cursor: 'pointer', minWidth: 150 }}>
                                {sortableHeader(<FormattedMessage defaultMessage="Completed" id="completed.cd3d7862" />, 'completedCount', sort)}
                            </th>
                            <th className="text-center" onClick={() => handleSort('totalMinutes')} style={{ cursor: 'pointer', minWidth: 150 }}>
                                {sortableHeader(<FormattedMessage defaultMessage="Total Time" id="total_time.ac5ce3ce" />, 'totalMinutes', sort)}
                            </th>
                            {lessonColumns.flatMap(lesson => lesson.resources.map(resource => (
                                <th
                                    className="text-center"
                                    key={resource.cellKey}
                                    onClick={() => handleSort(`cell:${resource.cellKey}`)}
                                    style={{ cursor: 'pointer', maxWidth: EXPANDED_COLUMN_WIDTH * 2, minWidth: EXPANDED_COLUMN_WIDTH }}
                                >
                                    {sortableHeader(
                                        <OverlayTrigger
                                            overlay={(
                                                <Tooltip id={`${resource.cellKey}-resource`}>
                                                    {resource.Resourcename}
                                                </Tooltip>
                                            )} placement="top"
                                        >
                                            <span className="text-truncate">
                                                {resource.Resourcename}
                                            </span>
                                        </OverlayTrigger>,
                                        `cell:${resource.cellKey}`,
                                        sort
                                    )}
                                </th>
                            )))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayStudents.map(student => (
                            <tr key={student.studentKey}>
                                <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                    <ReportToolTip copyText={student.Studentname} id={`${student.studentKey}-student`} tooltip={student.Studentname} trigger="hover">
                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {student.Studentname}
                                        </div>
                                    </ReportToolTip>
                                </td>
                                <td className="text-center">
                                    {`${student.completedCount}/${resourceCount}`}
                                </td>
                                <td className="text-center">
                                    {calculateHourMinFormat(student.totalMinutes)}
                                </td>
                                {lessonColumns.flatMap(lesson => lesson.resources.map(resource => {

                                    const cell = student.cells[resource.cellKey];
                                    return (
                                        <td className="text-center" key={resource.cellKey}>
                                            {cell ? (
                                                <ReportToolTip id={`${student.studentKey}-${resource.cellKey}`} tooltip={renderCellTooltip(cell)}>
                                                    {renderMetric(cell)}
                                                </ReportToolTip>
                                            ) : EMPTY}
                                        </td>
                                    );

                                }))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </Card>
    );

};
